<div class="page-layout">
  <app-toast></app-toast>

  <app-sidebar></app-sidebar>

  <div class="main-area">
    <app-navbar
      pageTitle="Batch Details"
      pageSubtitle="View and manage documents in this batch."
    ></app-navbar>

    <div class="content">
      <!-- Loading -->
      <div class="loading-state" *ngIf="isLoading()">
        <p>Loading batch details...</p>
      </div>

      <!-- Content -->
      <ng-container *ngIf="!isLoading() && batch() as b">
        <!-- Header -->
        <div class="batch-header">
          <div class="header-left">
            <button class="back-btn" (click)="goBack()">
              <svg width="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>

            <div class="header-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A855F7" stroke-width="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>

            <h2 class="batch-title">{{ b.name }}</h2>
            <span class="status-badge" [ngClass]="statusClass(b.status)">{{ b.status }}</span>
            <span class="elapsed-chip" *ngIf="b.startedAt" title="Time from Start Ingestion to completion">
              Elapsed: {{ batchElapsed(b) }}
            </span>
          </div>

          <div class="header-actions">
            <app-button variant="primary" size="md" [disabled]="isStartingIngestion() || !canStartIngestion(b.status) || documents().length === 0" (clicked)="onStartIngestion()">
              {{ isStartingIngestion() ? (isRestart(b.status) ? 'Restarting...' : 'Starting...') : (isRestart(b.status) ? 'Restart Ingestion' : 'Start Ingestion') }}
            </app-button>
            <button class="delete-btn" [disabled]="!canDelete(b.status)" (click)="onDelete()">
              {{ isDeleting() ? 'Deleting...' : 'Delete' }}
            </button>
          </div>
        </div>

        <!-- Documents Table -->
        <div class="table-card">
          <h3 class="table-title">Documents ({{ documents().length }})</h3>

          <table class="documents-table" *ngIf="documents().length > 0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Connector</th>
                <th>Classification</th>
                <th>Sub Classification</th>
                <th>Version</th>
                <th>Time</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let doc of documents()">
                <td class="doc-name-cell">
                  <span class="file-icon" [ngClass]="getDocIconClass(doc.fileName)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </span>
                  <button type="button" class="doc-name doc-name-button" (click)="openDocument(doc)">{{ doc.fileName }}</button>
                </td>
                <td>
                  <span class="connector-badge">
                    <span class="connector-icon">
                      <app-brand-icon [name]="connectorIcon(doc)" [size]="16"></app-brand-icon>
                    </span>
                    {{ getConnectorLabel(doc) }}
                  </span>
                </td>
                <td>{{ doc.classification || '-' }}</td>
                <td>{{ doc.subClassification || '-' }}</td>
                <td class="version-cell">
                  <ng-container *ngIf="versionLabel(doc) as label; else noVersion">
                    <span class="version-badge" [ngClass]="'version-' + doc.versionAction">{{ label }}</span>
                    <div class="version-summary">{{ versionSummary(doc) }}</div>
                  </ng-container>
                  <ng-template #noVersion>-</ng-template>
                </td>
                <td class="time-cell">
                  <button
                    *ngIf="hasTimings(doc); else noTimings"
                    type="button"
                    class="time-toggle"
                    [attr.aria-expanded]="isTimingsExpanded(doc.id)"
                    (click)="toggleTimings(doc.id)"
                  >
                    {{ docElapsed(doc) }}
                    <span class="doc-status-chevron" [class.open]="isTimingsExpanded(doc.id)">›</span>
                  </button>
                  <ng-template #noTimings>-</ng-template>
                  <table class="stage-timings" *ngIf="isTimingsExpanded(doc.id)">
                    <tr *ngFor="let row of stageRows(doc)">
                      <td>{{ row.label }}</td>
                      <td class="stage-duration">{{ row.duration }}</td>
                      <td class="stage-wait">{{ row.wait ? 'after ' + row.wait + ' in queue' : '' }}</td>
                    </tr>
                  </table>
                </td>
                <td>
                  <div class="doc-status-cell">
                    <button
                      *ngIf="hasDocError(doc); else staticStatusBadge"
                      type="button"
                      class="doc-status-badge doc-status-dropdown badge-failed"
                      [class.is-open]="isErrorExpanded(doc.id)"
                      [attr.aria-expanded]="isErrorExpanded(doc.id)"
                      (click)="toggleDocError(doc.id, $event)"
                    >
                      {{ docDisplayStatus(doc) }}
                      <span class="doc-status-chevron" [class.open]="isErrorExpanded(doc.id)">›</span>
                    </button>
                    <ng-template #staticStatusBadge>
                      <span class="doc-status-badge" [ngClass]="docStatusBadgeClass(doc)">{{ docDisplayStatus(doc) }}</span>
                    </ng-template>
                    <div class="doc-error-panel" *ngIf="hasDocError(doc) && isErrorExpanded(doc.id)">
                      <p class="doc-error-summary">{{ docErrorMessage(doc) }}</p>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="action-cell">
                    <button
                      class="view-details-btn"
                      [class.is-visible]="isCompleted(doc.status) && !isEmailItem(doc)"
                      [attr.aria-hidden]="!(isCompleted(doc.status) && !isEmailItem(doc))"
                      [disabled]="!(isCompleted(doc.status) && !isEmailItem(doc))"
                      (click)="viewDocumentAccess(doc)"
                    >View Details</button>
                    <button
                      class="doc-delete-btn"
                      *ngIf="!isEmailItem(doc)"
                      [disabled]="!canDeleteDocument(doc)"
                      [title]="doc.status === 'Processing' ? 'You can delete after processing completes.' : 'Delete this document'"
                      (click)="onDeleteDocument(doc)"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                        <path d="M10 11v6"></path>
                        <path d="M14 11v6"></path>
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="empty-state" *ngIf="documents().length === 0">
            <p>No documents in this batch yet.</p>
          </div>

          <div class="pagination" *ngIf="documents().length > 0 && totalDocumentPages > 1">
            <app-button variant="secondary" size="sm" [disabled]="documentPage() === 1 || isLoadingPage()" (clicked)="prevDocumentPage()">Previous</app-button>
            <span class="page-info">
              Page {{ documentPage() }} of {{ totalDocumentPages }}
              &middot; {{ b.totalDocuments }} document{{ b.totalDocuments === 1 ? '' : 's' }} total
            </span>
            <app-button variant="secondary" size="sm" [disabled]="documentPage() === totalDocumentPages || isLoadingPage()" (clicked)="nextDocumentPage()">Next</app-button>
          </div>
        </div>
      </ng-container>
    </div>
  </div>

  <app-ingest-progress-modal
    *ngIf="showIngestingModal()"
    [percent]="ingestPercent()"
    (close)="closeIngestingModal()"
  ></app-ingest-progress-modal>

  <app-batch-completed-modal
    *ngIf="showCompletedModal()"
    (cancel)="closeCompletedModal()"
    (viewAll)="onViewAllBatches()"
  ></app-batch-completed-modal>

  <app-delete-batch-modal
    *ngIf="showDeleteModal()"
    [isDeleting]="isDeleting()"
    (cancel)="closeDeleteModal()"
    (confirm)="confirmDelete()"
  ></app-delete-batch-modal>

  <app-delete-batch-modal
    *ngIf="docPendingDelete() as doc"
    title="Delete Document"
    [message]="'Do you want to delete \'' + doc.fileName + '\'? This will remove it, its indexed content, and its knowledge-graph data.'"
    [isDeleting]="!!deletingDocId()"
    (cancel)="closeDeleteDocumentModal()"
    (confirm)="confirmDeleteDocument()"
  ></app-delete-batch-modal>

  <div class="doc-preview-overlay" *ngIf="preview() as view" (click)="closePreview()">
    <div class="doc-preview-box" role="dialog" aria-modal="true" [attr.aria-label]="view.fileName" (click)="$event.stopPropagation()">
      <div class="doc-preview-header">
        <h2 class="doc-preview-title">{{ view.fileName }}</h2>
        <button type="button" class="doc-preview-close" (click)="closePreview()" aria-label="Close">×</button>
      </div>
      <div class="doc-preview-body">
        <p class="doc-preview-status" *ngIf="view.loading">Opening document…</p>
        <p class="doc-preview-status" *ngIf="!view.loading && view.kind === 'unavailable'">{{ view.message }}</p>
        <pre class="doc-preview-text" *ngIf="!view.loading && view.kind === 'text'">{{ view.text }}</pre>
        <img class="doc-preview-image" *ngIf="!view.loading && view.kind === 'image' && view.url" [src]="view.url" [alt]="view.fileName" />
        <iframe class="doc-preview-frame" *ngIf="!view.loading && view.kind === 'pdf' && view.frameUrl" [src]="view.frameUrl" [title]="view.fileName"></iframe>
      </div>
    </div>
  </div>
</div>